import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getSites,
  getSite,
  getSiteWithOptions,
  getSiteOptions,
  createSite,
  updateSite,
  deleteSite,
  getAccounts,
  getSiteChangelog,
  getUsergroupAdmins,
  createLocationForSite,
} from '../api';
import { siteKeys } from './queryKeys';
import type { SitesFilters, CreateSiteInput, UpdateSiteInput } from '../types';

/**
 * Helper to extract error message from unknown error
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch sites with filters.
 *
 * `options.enabled` gates the query (default true) — callers that mount this
 * hook persistently (e.g. modals kept mounted behind an `open` flag) pass
 * `{ enabled: open && !!accountId }` so it doesn't fetch while closed or before
 * an account is known. Mirrors the `useQuotes(params, { enabled })` pattern.
 */
export function useSites(filters: SitesFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: siteKeys.list(filters),
    queryFn: () => getSites(filters),
    enabled: options.enabled !== false,
  });
}

/**
 * Hook to fetch a single site by ID
 */
export function useSite(siteId: string | undefined) {
  return useQuery({
    queryKey: siteKeys.detail(siteId!),
    queryFn: () => getSite(siteId!),
    enabled: !!siteId,
  });
}

/**
 * Hook to fetch a site with edit options
 */
export function useSiteWithOptions(siteId: string | undefined) {
  return useQuery({
    queryKey: siteKeys.detailWithOptions(siteId!),
    queryFn: () => getSiteWithOptions(siteId!),
    enabled: !!siteId,
  });
}

/**
 * Hook to fetch site options for create modal
 */
export function useSiteOptions() {
  return useQuery({
    queryKey: siteKeys.options(),
    queryFn: getSiteOptions,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to fetch accounts for filter dropdown
 */
export function useAccounts() {
  return useQuery({
    queryKey: siteKeys.accounts(),
    queryFn: getAccounts,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to fetch changelog for a site
 */
export function useSiteChangelog(siteId: string | undefined) {
  return useQuery({
    queryKey: siteKeys.changelog(siteId!),
    queryFn: () => getSiteChangelog(siteId!),
    enabled: !!siteId,
  });
}

/**
 * Hook to fetch admin users for selected usergroups
 */
export function useUsergroupAdmins(usergroupIds: string[]) {
  return useQuery({
    queryKey: [...siteKeys.all, 'admins', usergroupIds],
    queryFn: () => getUsergroupAdmins(usergroupIds),
    enabled: usergroupIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new site
 */
export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSiteInput) => createSite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
      toast.success('Site created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create site', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update a site
 */
export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteId, data }: { siteId: string; data: UpdateSiteInput }) =>
      updateSite(siteId, data),
    onSuccess: (_site, variables) => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: siteKeys.detail(variables.siteId) });
      queryClient.invalidateQueries({ queryKey: siteKeys.detailWithOptions(variables.siteId) });
      queryClient.invalidateQueries({ queryKey: siteKeys.changelog(variables.siteId) });
      toast.success('Site updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update site', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete a site
 */
export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (siteId: string) => deleteSite(siteId),
    onSuccess: (_result, siteId) => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
      toast.success('Site deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete site', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to create a location for a site (quick add)
 */
export function useCreateLocationForSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteId, accountId, data }: { siteId: string; accountId: string; data: { name: string; roomNumber?: string } }) =>
      createLocationForSite(siteId, accountId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: siteKeys.options() });
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: siteKeys.details() });
      queryClient.invalidateQueries({ queryKey: siteKeys.detailWithOptions(variables.siteId) });
      toast.success('Location created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create location', { description: getErrorMessage(error) });
    },
  });
}
